import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BarChart3, Users, MessageCircle, Activity, Trophy } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePageSeo } from '../hooks/usePageSeo';
import {
  aggregateCategoryForums,
  buildCategorySeoParagraph,
  latestForumUpdateIso,
} from '../utils/categoryAggregates';
import { calculateForumScore } from '../utils/forumScore';
import { findCategoryBySlug, createCategorySlug } from '../utils/urlHelpers';
import { absoluteUrl } from '../utils/seo';
import { buildOgImageUrl } from '../utils/ogImage';
import { formatChangePercent } from '../utils/forumStatChange';
import type { ForumBase } from '../types/forum';

interface Props {
  darkMode: boolean;
}

const CategoryStatsSeoPage: React.FC<Props> = ({ darkMode }) => {
  const { slug } = useParams<{ slug: string }>();
  const [forums, setForums] = useState<ForumBase[]>([]);
  const [categoryName, setCategoryName] = useState('');
  const [description, setDescription] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [{ data: cats }, { data: allForums }] = await Promise.all([
        supabase.from('categories').select('name, description'),
        supabase.from('forums').select('*').eq('is_active', true),
      ]);
      const categories = (cats || []).map((c) => ({ name: c.name }));
      const decoded = decodeURIComponent(slug || '');
      const name = findCategoryBySlug(decoded, categories) || decoded;
      setCategoryName(name);
      const catRow = (cats || []).find((c) => c.name === name);
      setDescription(catRow?.description ?? null);
      setForums((allForums || []).filter((f) => f.category === name));
      setLoading(false);
    })();
  }, [slug]);

  const agg = useMemo(() => aggregateCategoryForums(forums), [forums]);
  const lastIso = useMemo(() => latestForumUpdateIso(forums), [forums]);
  const topForums = useMemo(
    () =>
      [...forums]
        .sort(
          (a, b) =>
            calculateForumScore(b.topics_count, b.posts_count, b.members_count, b.founded_year) -
            calculateForumScore(a.topics_count, a.posts_count, a.members_count, a.founded_year)
        )
        .slice(0, 10),
    [forums]
  );

  const seoDesc = useMemo(
    () => buildCategorySeoParagraph(categoryName, agg, lastIso, description),
    [categoryName, agg, lastIso, description]
  );

  const canonical = `/kategori/${createCategorySlug(categoryName)}/istatistikler`;

  usePageSeo({
    title: `${categoryName} Forum İstatistikleri — Üye, Konu ve İleti Özeti`,
    description: seoDesc.slice(0, 320),
    keywords: `${categoryName}, ${categoryName} forum istatistikleri, forum kategorisi, türk forumları`,
    canonical: absoluteUrl(canonical),
    ogImage: categoryName
      ? buildOgImageUrl({
          title: `${categoryName} İstatistikleri`,
          subtitle: `${agg.forumCount} forum · ${agg.totalMembers.toLocaleString('tr-TR')} üye`,
          line1: topForums[0] ? `#1 ${topForums[0].name}` : undefined,
          badge: 'Kategori özeti',
        })
      : undefined,
  });

  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm';
  const memberPct = formatChangePercent(agg.totalMembers, agg.previousTotalMembers);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (!categoryName || forums.length === 0) {
    return (
      <div className={`min-h-screen p-8 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
        <p>Kategori bulunamadı.</p>
        <Link to="/category" className="text-indigo-500 mt-4 inline-block">
          Kategorilere dön
        </Link>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-4xl">
        <Link
          to={`/category/${createCategorySlug(categoryName)}`}
          className={`inline-flex items-center gap-2 mb-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
        >
          <ArrowLeft size={18} />
          {categoryName} forum listesi
        </Link>

        <header className={`rounded-2xl border p-6 mb-6 ${card}`}>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <BarChart3 className="text-indigo-500" />
            {categoryName} — forum istatistikleri
          </h1>
          <p className={`mt-3 text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {seoDesc}
          </p>
        </header>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { icon: Users, label: 'Toplam üye', value: agg.totalMembers, pct: memberPct },
            { icon: MessageCircle, label: 'Konu', value: agg.totalTopics, pct: formatChangePercent(agg.totalTopics, agg.previousTotalTopics) },
            { icon: Activity, label: 'İleti', value: agg.totalPosts, pct: formatChangePercent(agg.totalPosts, agg.previousTotalPosts) },
            { icon: Trophy, label: 'Forum sayısı', value: agg.forumCount, pct: null },
          ].map(({ icon: Icon, label, value, pct }) => (
            <div key={label} className={`rounded-xl border p-4 min-w-0 ${card}`}>
              <Icon size={20} className="text-indigo-500 mb-2" />
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
              <p className="text-xl font-bold tabular-nums truncate">{value.toLocaleString('tr-TR')}</p>
              {pct && <p className="text-green-500 text-xs mt-1 font-medium">{pct}</p>}
            </div>
          ))}
        </div>

        <section className={`rounded-xl border p-5 ${card}`}>
          <h2 className="text-lg font-semibold mb-4">Skor sıralaması (ilk 10)</h2>
          <ol className="space-y-2">
            {topForums.map((f, i) => (
              <li key={f.id} className="flex items-center gap-3">
                <span className="font-bold text-indigo-500 w-6">{i + 1}</span>
                <Link
                  to={`/forum/${f.forum_id}`}
                  className={`font-medium hover:underline flex-1 min-w-0 truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}
                >
                  {f.name}
                </Link>
                <span className="text-sm tabular-nums shrink-0">
                  {calculateForumScore(f.topics_count, f.posts_count, f.members_count, f.founded_year).toLocaleString('tr-TR')}
                </span>
              </li>
            ))}
          </ol>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/category/${createCategorySlug(categoryName)}`}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
            >
              Tüm forumları gör
            </Link>
            <Link
              to="/compare-categories"
              className={`px-4 py-2 rounded-lg border text-sm ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
            >
              Kategori karşılaştır
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CategoryStatsSeoPage;
